/**
 * BookingsListPanel Component
 *
 * Displays a sorted list of bookings for the current month/filter.
 * Each item shows casa (recurso), cliente, rango de fechas y estado.
 * Clicking a booking navigates to the agent's calendar bookings page.
 *
 * Validates: Requirements 5.1, 5.2, 5.3, 5.4, 5.5, 5.6
 * - Requirement 5.1: Display bookings list panel showing all bookings for the current month.
 * - Requirement 5.2: Each booking shows casa (recurso), cliente, date range, status badge.
 * - Requirement 5.3: Bookings sorted by start date ascending.
 * - Requirement 5.4: When resource filter is active, only matching bookings are displayed.
 * - Requirement 5.5: Empty state "No hay reservas para este período".
 * - Requirement 5.6: Click navigates to /app/agents/{agentId}/calendar/bookings.
 */

import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { bookingChatPath } from './bookingChatLink';
import { tokens } from '../../../design-system/tokens';
import type { CalendarViewBooking } from '../calendarView.types';
import { airbnbStayKind, airbnbStayShortLabel } from '../calendarView.utils';
import { getStatusColors } from './BookingIndicator';
import { BookingOriginIcon, bookingOriginTitle } from './BookingOriginIcon';

// ============================================
// TYPES
// ============================================

export interface BookingsListPanelProps {
  /** Bookings to display (will be sorted by startDate ascending) */
  bookings: CalendarViewBooking[];
  /** Mensaje cuando no hay ítems (default: período genérico) */
  emptyMessage?: string;
  /** Cancela una reserva interna sin navegar al detalle. */
  onCancel?: (booking: CalendarViewBooking) => void;
  cancellingId?: string | null;
}

// ============================================
// HELPERS
// ============================================

/**
 * Formats a date range in a readable Spanish-friendly format.
 * Examples:
 * - Same month: "10 Jun - 12 Jun 2025"
 * - Different months: "28 Jun - 2 Jul 2025"
 */
function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const dayStart = start.getUTCDate();
  const dayEnd = end.getUTCDate();

  const monthNames = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
  ];

  const monthStart = monthNames[start.getUTCMonth()];
  const monthEnd = monthNames[end.getUTCMonth()];
  const year = end.getUTCFullYear();

  return `${dayStart} ${monthStart} - ${dayEnd} ${monthEnd} ${year}`;
}

// ============================================
// STYLES
// ============================================

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[0],
  borderRadius: tokens.borderRadius.lg,
  border: `1px solid ${tokens.colors.neutral[200]}`,
  backgroundColor: tokens.colors.neutral[50],
  overflow: 'hidden',
};

const emptyStateStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: tokens.spacing[8],
  color: tokens.colors.neutral[500],
  fontSize: tokens.typography.fontSize.sm,
  fontStyle: 'italic',
};

const itemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: tokens.spacing[3],
  padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
  borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
  cursor: 'pointer',
  transition: `background-color ${tokens.transitions.fast}`,
  backgroundColor: 'transparent',
};

const customerNameStyle: React.CSSProperties = {
  fontSize: tokens.typography.fontSize.xs,
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.colors.neutral[600],
  lineHeight: tokens.typography.lineHeight.tight,
};

const casaRowStyle: React.CSSProperties = {
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.colors.neutral[900],
  lineHeight: tokens.typography.lineHeight.tight,
};

const casaLabelStyle: React.CSSProperties = {
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.colors.neutral[600],
  marginRight: tokens.spacing[1],
};

const dateRangeStyle: React.CSSProperties = {
  fontSize: tokens.typography.fontSize.xs,
  color: tokens.colors.neutral[600],
  whiteSpace: 'nowrap',
};

// ============================================
// SUB-COMPONENTS
// ============================================

interface StatusBadgeProps {
  status: CalendarViewBooking['status'];
}

function StatusBadge({ status }: StatusBadgeProps) {
  const colors = getStatusColors(status);

  const labelMap: Record<CalendarViewBooking['status'], string> = {
    confirmed: 'Confirmada',
    pending_deposit: 'Pendiente de seña',
    human_supervision: 'Supervisión humana',
    completed: 'Completada (hist.)',
    cancelled: 'Cancelada',
    auto_cancelled: 'Cancelada auto.',
  };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: `${tokens.spacing[0]} ${tokens.spacing[2]}`,
        borderRadius: tokens.borderRadius.full,
        backgroundColor: colors.background,
        color: colors.text,
        fontSize: tokens.typography.fontSize.xs,
        fontWeight: tokens.typography.fontWeight.medium,
        lineHeight: tokens.typography.lineHeight.tight,
        whiteSpace: 'nowrap',
      }}
    >
      {labelMap[status]}
    </span>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * BookingsListPanel — Displays a sorted list of bookings with details.
 *
 * Bookings are sorted by startDate ascending. Each item shows casa (recurso),
 * cliente, formatted date range, and a status badge. Clicking an item
 * navigates to the agent's calendar bookings page.
 *
 * @example
 * ```tsx
 * <BookingsListPanel bookings={filteredBookings} />
 * ```
 */
export function BookingsListPanel({
  bookings,
  emptyMessage,
  onCancel,
  cancellingId,
}: BookingsListPanelProps) {
  const nav = useNavigate();

  // Sort bookings by startDate ascending
  const sortedBookings = useMemo(() => {
    return [...bookings].sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
  }, [bookings]);

  if (sortedBookings.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={emptyStateStyle}>
          {emptyMessage ?? 'No hay reservas para este período'}
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {sortedBookings.map((booking, index) => {
        const stayKind = airbnbStayKind(booking);
        const stayLabel = airbnbStayShortLabel(booking) ?? booking.customerName;
        return (
        <div
          key={booking.id}
          role="button"
          tabIndex={0}
          aria-label={`Reserva en casa ${booking.resourceName}, cliente ${stayLabel}`}
          style={{
            ...itemStyle,
            // Remove bottom border on last item
            ...(index === sortedBookings.length - 1 ? { borderBottom: 'none' } : {}),
          }}
          onClick={() => nav(`/app/agents/${booking.agentId}/calendar/bookings`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              nav(`/app/agents/${booking.agentId}/calendar/bookings`);
            }
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = tokens.colors.neutral[100];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {/* Izquierda: casa (recurso) + cliente */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[1], minWidth: 0, flex: 1 }}>
            <div style={casaRowStyle}>
              <span style={casaLabelStyle}>Casa:</span>
              <BookingOriginIcon
                bookingOrigin={booking.bookingOrigin}
                otaChannel={booking.otaChannel}
                conversationChannel={booking.conversationChannel}
                size={15}
              />
              <span>{booking.resourceName}</span>
            </div>
            <span
              style={{
                ...customerNameStyle,
                fontWeight:
                  stayKind === 'reserved'
                    ? tokens.typography.fontWeight.extrabold
                    : stayKind === 'block'
                      ? tokens.typography.fontWeight.normal
                      : customerNameStyle.fontWeight,
              }}
            >
              {stayLabel} ·{' '}
              {bookingOriginTitle({
                bookingOrigin: booking.bookingOrigin,
                otaChannel: booking.otaChannel,
                conversationChannel: booking.conversationChannel,
              })}
            </span>
            {booking.alsoBlockedOnAirbnb ? (
              <span style={{ fontSize: tokens.typography.fontSize.xs, color: tokens.colors.warning.dark, lineHeight: tokens.typography.lineHeight.tight }}>
                También bloqueado en Airbnb
              </span>
            ) : null}
            {booking.conversationId ? (
              <span style={{ fontSize: tokens.typography.fontSize.xs, color: tokens.colors.neutral[600], lineHeight: tokens.typography.lineHeight.tight }}>
                {booking.sourceMessagePreview ? `“${booking.sourceMessagePreview}” · ` : null}
                <Link
                  to={bookingChatPath(booking.conversationId, booking.sourceMessageId)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{ color: tokens.colors.primary[600], fontWeight: tokens.typography.fontWeight.semibold }}
                >
                  Ver mensaje del chat
                </Link>
              </span>
            ) : null}
          </div>

          {/* Right side: date range + status badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[3], flexShrink: 0 }}>
            <span style={dateRangeStyle}>{formatDateRange(booking.startDate, booking.endDate)}</span>
            <StatusBadge status={booking.status} />
            {onCancel &&
            booking.bookingOrigin !== 'ota' &&
            booking.status !== 'cancelled' &&
            booking.status !== 'auto_cancelled' ? (
              <button
                type="button"
                disabled={cancellingId === booking.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(booking);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                style={{
                  border: `1px solid ${tokens.colors.error.main}`,
                  color: tokens.colors.error.dark,
                  background: tokens.colors.neutral[50],
                  borderRadius: tokens.borderRadius.md,
                  padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                  cursor: cancellingId === booking.id ? 'wait' : 'pointer',
                  fontSize: tokens.typography.fontSize.xs,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  whiteSpace: 'nowrap',
                }}
              >
                {cancellingId === booking.id ? 'Cancelando…' : 'Cancelar'}
              </button>
            ) : null}
          </div>
        </div>
        );
      })}
    </div>
  );
}

export default BookingsListPanel;
